import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AgGridReact } from "ag-grid-react";
import AGTable from "@/components/AGTable/AGTable";
import { useSchemaProcessor } from "@/hooks";
import { buildRows } from "./schemaToRows";
import { rowsToData } from "./rowsToData";
import { validateDraft } from "./validation";
import { useDraftState } from "./useDraftState";
import { buildStepPlan, type Step } from "./buildStepPlan";
import { FieldCellRenderer } from "./cells/FieldCellRenderer";
import { ValueCellRenderer } from "./cells/ValueCellRenderer";
import { ValueCellEditor } from "./cells/ValueCellEditor";
import { StatusCellRenderer } from "./cells/StatusCellRenderer";
import {
  ActionsCellRenderer,
  type ActionsCellContext,
} from "./cells/ActionsCellRenderer";
import { DiffPreviewCell } from "./cells/DiffPreviewCell";
import { StepBar } from "./StepBar";
import { SaveBar } from "./SaveBar";
import { buildDefaultValue } from "./seedRequiredDefaults";
import type { BranchDiff, FormRow } from "./types";

// Depth-based tint: each nesting level shifts background slightly so 5–6 levels
// of nesting remain visually parseable.
const DEPTH_BG_BY_LEVEL: Record<number, string> = {
  0: "#0a1530",
  1: "#0c1838",
  2: "#0e1b40",
  3: "#101e48",
  4: "#122150",
  5: "#142458",
};

const POINTER_STYLE = { cursor: "pointer" } as const;

export interface RpcTreeFormProps {
  schema: any;
  initialData: any;
  mode: "wizard" | "edit";
  populateDefaults?: boolean;
  isSubmitting: boolean;
  onSubmit: (args: any) => Promise<void>;
  onDraftChange?: (draft: any) => void;
  hideHeaders?: boolean;
  footer?: React.ReactNode;
}

function flattenRows(rows: FormRow[]): FormRow[] {
  const out: FormRow[] = [];
  const walk = (r: FormRow) => {
    out.push(r);
    if (r.children) r.children.forEach(walk);
  };
  rows.forEach(walk);
  return out;
}

function findRowDeep(rows: FormRow[], id: string): FormRow | undefined {
  for (const r of rows) {
    if (r.id === id) return r;
    if (r.children) {
      const inner = findRowDeep(r.children, id);
      if (inner) return inner;
    }
  }
  return undefined;
}

export const RpcTreeForm = (props: RpcTreeFormProps) => {
  const { schema, initialData, mode, populateDefaults = false, isSubmitting, onSubmit, onDraftChange, hideHeaders = false, footer } = props;
  const { processedSchema, isLoading: isProcessing, error: schemaError } =
    useSchemaProcessor(schema);

  const { state, dispatch, commit } = useDraftState({
    initialData,
    schema: processedSchema,
    populateDefaults,
    onDraftChange,
  });

  const gridRef = useRef<AgGridReact>(null);

  const validation = useMemo(() => {
    if (!processedSchema) {
      return { errors: [], blockingErrors: [], errorsByRowId: new Map<string, { message: string; severity: "error" | "warning" }>() };
    }
    return validateDraft({ draft: state.draft, schema: processedSchema });
  }, [processedSchema, state.draft]);

  const [pendingDiffs, setPendingDiffs] = useState<Map<string, BranchDiff>>(new Map());

  const rows = useMemo(() => {
    if (!processedSchema) return [];
    return buildRows({
      schema: processedSchema,
      data: state.draft,
      dirtyIds: state.dirtyIds,
      errorsByRowId: validation.errorsByRowId,
      pendingDiffs,
    });
  }, [processedSchema, state.draft, state.dirtyIds, validation.errorsByRowId, pendingDiffs]);

  const flatRows = useMemo(() => flattenRows(rows), [rows]);

  const stepPlan: Step[] = useMemo(
    () => (mode === "wizard" && processedSchema ? buildStepPlan(processedSchema, state.draft) : []),
    [mode, processedSchema, state.draft]
  );
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = stepPlan[stepIndex];

  // Clamp stepIndex if plan shrinks
  useEffect(() => {
    if (mode === "wizard" && stepIndex >= stepPlan.length && stepPlan.length > 0) {
      setStepIndex(stepPlan.length - 1);
    }
  }, [mode, stepPlan.length, stepIndex]);

  const isExternalFilterPresent = useCallback(
    () => mode === "wizard" && !!currentStep && currentStep.rowIdPrefix !== "",
    [mode, currentStep]
  );
  const doesExternalFilterPass = useCallback(
    (node: any) => {
      if (!currentStep || currentStep.rowIdPrefix === "") return true;
      const id = node.data?.id as string | undefined;
      if (!id) return false;
      const prefix = currentStep.rowIdPrefix;
      return (
        id === prefix ||
        id.startsWith(prefix + ".") ||
        prefix.startsWith(id + ".")
      );
    },
    [currentStep]
  );

  // Re-apply filter + auto-expand on step change
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.onFilterChanged();
    if (mode === "wizard" && currentStep && currentStep.rowIdPrefix !== "") {
      api.forEachNode((n: any) => {
        const id = n.data?.id as string | undefined;
        if (id && (id.startsWith(currentStep.rowIdPrefix) || currentStep.rowIdPrefix.startsWith(id + "."))) {
          n.setExpanded(true);
        }
      });
    }
  }, [mode, currentStep, rows]);

  const stepBlockingErrors = useMemo(() => {
    if (mode !== "wizard" || !currentStep) return 0;
    if (currentStep.rowIdPrefix === "") return validation.blockingErrors.length;
    return validation.blockingErrors.filter((e) =>
      e.rowId === currentStep.rowIdPrefix ||
      e.rowId.startsWith(currentStep.rowIdPrefix + ".")
    ).length;
  }, [mode, currentStep, validation.blockingErrors]);

  const [editorAnchor, setEditorAnchor] = useState<{ el: HTMLElement; row: FormRow } | null>(null);

  const onCellClicked = useCallback((e: any) => {
    if (e.colDef.field !== "value") return;
    const row = e.data as FormRow | undefined;
    if (!row) return;
    if (row.fieldType === "object" || row.fieldType === "array" || row.fieldType === "anyOfTabs") return;
    if (row.isDisabled) return;
    setEditorAnchor({ el: e.event.target as HTMLElement, row });
  }, []);

  const actionsContext: ActionsCellContext = useMemo(
    () => ({
      draft: state.draft,
      addArrayItem: (path, item) => dispatch({ type: "addArrayItem", path, item }),
      removeArrayItem: (path, index) => dispatch({ type: "removeArrayItem", path, index }),
      addOptionalField: (parentPath, key, defaultValue) =>
        dispatch({ type: "addOptionalField", parentPath, key, defaultValue }),
      removeOptionalField: (parentPath, key) =>
        dispatch({ type: "removeOptionalField", parentPath, key }),
      toggleOptionalSection: (path, enabled, defaultBranchData) =>
        dispatch({ type: "toggleOptionalSection", path, enabled, defaultBranchData }),
      commit,
    }),
    [state.draft, dispatch, commit]
  );

  const previewBranchSwitch = useCallback(
    (row: FormRow, toBranchIndex: number) => {
      if (!row.oneOfBranches || row.oneOfBranches.length === 0) return;
      const toBranch = row.oneOfBranches[toBranchIndex]?.schema;
      if (!toBranch) return;
      // First-time selection (no prior branch) → no diff to preview; commit directly.
      if (row.activeOneOfIndex === undefined || row.activeOneOfIndex === null) {
        const defaults = buildDefaultValue(toBranch);
        commit(row.path, defaults, row.label);
        return;
      }
      const fromIndex = row.activeOneOfIndex;
      if (toBranchIndex === fromIndex) return;
      const fromBranch = row.oneOfBranches[fromIndex]?.schema;
      if (!fromBranch) return;
      const fromKeys = new Set(Object.keys(fromBranch.properties ?? {}).filter((k) => !k.startsWith("__")));
      const toKeys = new Set(Object.keys(toBranch.properties ?? {}).filter((k) => !k.startsWith("__")));
      const removed = [...fromKeys].filter((k) => !toKeys.has(k));
      const added = [...toKeys].filter((k) => !fromKeys.has(k));
      setPendingDiffs((prev) => {
        const next = new Map(prev);
        next.set(row.id, {
          fromTitle: row.oneOfBranches![fromIndex]?.title ?? `Option ${fromIndex + 1}`,
          toTitle: row.oneOfBranches![toBranchIndex]?.title ?? `Option ${toBranchIndex + 1}`,
          toBranchIndex,
          removed,
          added,
          parentRowId: row.id,
        });
        return next;
      });
    },
    [commit]
  );

  const applyPendingDiff = useCallback(
    (parentRowId: string) => {
      const diff = pendingDiffs.get(parentRowId);
      if (!diff) return;
      const target = rows.find((r) => r.id === parentRowId) ?? findRowDeep(rows, parentRowId);
      if (!target) return;
      const branchSchema = target.oneOfBranches?.[diff.toBranchIndex]?.schema;
      const defaults = buildDefaultValue(branchSchema);
      commit(target.path, defaults, target.label);
      setPendingDiffs((prev) => {
        const next = new Map(prev);
        next.delete(parentRowId);
        return next;
      });
    },
    [pendingDiffs, rows, commit]
  );

  const cancelPendingDiff = useCallback((parentRowId: string) => {
    setPendingDiffs((prev) => {
      if (!prev.has(parentRowId)) return prev;
      const next = new Map(prev);
      next.delete(parentRowId);
      return next;
    });
  }, []);

  const diffCellContext = useMemo(
    () => ({ applyPendingDiff, cancelPendingDiff }),
    [applyPendingDiff, cancelPendingDiff]
  );

  const gridContext = useMemo(
    () => ({ ...actionsContext, ...diffCellContext }),
    [actionsContext, diffCellContext]
  );

  const colDefs = useMemo(
    () => [
      {
        field: "value",
        headerName: "Value",
        flex: 2,
        cellRenderer: ValueCellRenderer,
        sortable: false,
        filter: false,
        editable: false,
        cellStyle: (params: any) => {
          const r = params.data as FormRow | undefined;
          if (!r) return undefined;
          if (r.fieldType === "object" || r.fieldType === "array" || r.fieldType === "anyOfTabs") return undefined;
          return POINTER_STYLE;
        },
      },
      { field: "status", headerName: "Status", width: 140, cellRenderer: StatusCellRenderer, sortable: false, filter: false },
      { field: "actions", headerName: "", width: 140, cellRenderer: ActionsCellRenderer, sortable: false, filter: false },
    ],
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Field",
      minWidth: 320,
      filter: "agTextColumnFilter",
      cellRendererParams: { suppressCount: true, innerRenderer: FieldCellRenderer },
    }),
    []
  );

  const getRowStyle = useCallback((p: any): any => {
    const row = p.data as FormRow | undefined;
    const level = p.node?.level ?? 0;
    const base: Record<string, string> = {
      backgroundColor: DEPTH_BG_BY_LEVEL[Math.min(level, 5)] ?? "#142458",
    };
    if (!row) return base;
    if (row.errorMessage)
      return {
        ...base,
        borderLeft: `3px solid ${row.errorSeverity === "warning" ? "#ff9800" : "#ff1744"}`,
      };
    if (row.isDirty) return { ...base, borderLeft: "3px solid #ffb74d" };
    return base;
  }, []);

  const handleSave = useCallback(async () => {
    if (!processedSchema) return;
    const args = rowsToData(state.draft, processedSchema);
    await onSubmit(args);
  }, [processedSchema, state.draft, onSubmit]);

  const handleJumpToFirstError = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || validation.blockingErrors.length === 0) return;
    const firstRowId = validation.blockingErrors[0].rowId;
    // Expand each ancestor along the id path
    const parts = firstRowId.split(".");
    for (let i = 1; i < parts.length; i += 1) {
      const ancestorId = parts.slice(0, i).join(".");
      const ancestor = api.getRowNode(ancestorId);
      if (ancestor && !ancestor.expanded) ancestor.setExpanded(true);
    }
    requestAnimationFrame(() => {
      const target = api.getRowNode(firstRowId);
      if (target) {
        api.ensureNodeVisible(target, "middle");
        api.flashCells({ rowNodes: [target] });
      }
    });
  }, [validation.blockingErrors]);

  if (isProcessing) {
    return <div style={{ padding: 16 }}>Loading form…</div>;
  }
  if (schemaError) {
    return <div style={{ padding: 16, color: "#e57373" }}>Error: {schemaError.message}</div>;
  }
  if (!processedSchema) return null;

  const goToNextStep = () => setStepIndex((i) => Math.min(stepPlan.length - 1, i + 1));
  const goToPrevStep = () => setStepIndex((i) => Math.max(0, i - 1));

  const arrayAddLabel =
    mode === "wizard" && currentStep?.scope === "array-item"
      ? currentStep.arrayAddLabel
      : undefined;
  const onAddArrayItem =
    mode === "wizard" && currentStep?.scope === "array-item" && currentStep.arrayParentPath
      ? () => {
          const arrParent = stepPlan[stepIndex].arrayParentPath!;
          const arrSchema = (() => {
            let s: any = processedSchema;
            for (const seg of arrParent) {
              s = s?.properties?.[seg as string];
            }
            return s;
          })();
          dispatch({
            type: "addArrayItem",
            path: arrParent,
            item: buildDefaultValue(arrSchema?.items),
          });
        }
      : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {mode === "wizard" && stepPlan.length > 0 && currentStep && (
        <StepBar
          steps={stepPlan}
          activeIndex={stepIndex}
          blockingErrors={stepBlockingErrors}
          isLastStep={stepIndex === stepPlan.length - 1}
          isOptional={currentStep.isOptional}
          isSubmitting={isSubmitting}
          onBack={goToPrevStep}
          onNext={goToNextStep}
          onSkip={goToNextStep}
          onAddArrayItem={onAddArrayItem}
          arrayAddLabel={arrayAddLabel}
          onSubmit={handleSave}
        />
      )}

      {mode === "edit" && (
        <SaveBar
          changeCount={state.changeLog.length}
          errorCount={validation.blockingErrors.length}
          isSubmitting={isSubmitting}
          onDiscard={() => dispatch({ type: "discard" })}
          onSave={handleSave}
          onJumpToFirstError={handleJumpToFirstError}
        />
      )}

      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <AGTable
          gridRef={gridRef}
          rowData={flatRows.length > 0 ? rows : []}
          colDefs={colDefs}
          defaultColDef={{ resizable: true, sortable: false, filter: false }}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType={"singleColumn" as any}
          treeData={true}
          animateRows={true}
          sideBar={false}
          containerStyle={{ height: "100%", overflow: "auto" }}
          headerHeight={hideHeaders ? 0 : undefined}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          getRowStyle={getRowStyle}
          context={gridContext}
          isFullWidthRow={(p: any) => p.rowNode?.data?.fieldType === "diffPreview"}
          fullWidthCellRenderer={DiffPreviewCell}
          onCellClicked={onCellClicked}
          onCellDoubleClicked={onCellClicked}
        />
      </div>

      {footer}

      <div
        style={{
          height: 3,
          flexShrink: 0,
          backgroundColor:
            validation.blockingErrors.length > 0 ? "#ff1744" : "#00c853",
          transition: "background-color 200ms",
          boxShadow:
            validation.blockingErrors.length > 0
              ? "0 0 8px rgba(255, 23, 68, 0.6)"
              : "0 0 8px rgba(0, 200, 83, 0.5)",
        }}
        title={
          validation.blockingErrors.length > 0
            ? `${validation.blockingErrors.length} validation error${validation.blockingErrors.length === 1 ? "" : "s"}`
            : "Ready to submit"
        }
      />

      {editorAnchor && (
        <ValueCellEditor
          anchorEl={editorAnchor.el}
          row={editorAnchor.row}
          onCommit={(value) => {
            commit(editorAnchor.row.path, value, editorAnchor.row.label);
            setEditorAnchor(null);
          }}
          onSwitchBranch={
            editorAnchor.row.fieldType === "oneOf"
              ? (branchIndex) => {
                  previewBranchSwitch(editorAnchor.row, branchIndex);
                  setEditorAnchor(null);
                }
              : undefined
          }
          onCancel={() => setEditorAnchor(null)}
        />
      )}
    </div>
  );
};
